import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Radio,
  RadioGroup,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { FaCheckCircle, FaRegUser } from "react-icons/fa";
import { LuIndianRupee } from "react-icons/lu";
import { SlBriefcase } from "react-icons/sl";
import { useDispatch, useSelector } from "react-redux";
// Custom radio button group component for Loan Type
import { FaSackDollar } from "react-icons/fa6";
import { updateStatusForTimeline } from "../../../api/timelineService";
import { createLoanApplication } from "../../../api/userService";
import { purposeOfLoan } from "../../../constants";
import useCustomToast from "../../../customHooks/useCustomToast";
import { setUserDetails, setUserTimelineStep } from "../../../Redux/userSlice";
import { formatNumberWithCommas } from "../../../utils/UTILITY_METHODS";

const LoanTypeOption = ({ icon, label, isActive, ...rest }) => {
  return (
    <Button
      variant="outline"
      width="full"
      boxShadow="rgba(17, 17, 26, 0.1) 0px 0px 4px;"
      outline="none"
      justifyContent={"space-between"}
      alignItems={"center"}
      fontWeight={"normal"}
      paddingY={"5"}
      size={"sm"}
      _hover={{
        borderColor: "teal.400",
        boxShadow: "none",
      }}
      _active={{
        borderColor: "teal.500",
        boxShadow: "none",
      }}
      borderColor={isActive ? "teal.400" : "transparent"} // teal border when active
      {...rest}
    >
      <Box display={"flex"} columnGap={"2"} alignItems={"center"}>
        <Icon color="orange.300" as={icon} />
        <Text pl={"2"}>{label}</Text>
      </Box>

      {isActive && <Icon as={FaCheckCircle} color="teal.500" />}
    </Button>
  );
};

const LoanDetails = () => {
  const { customerUUID } = useSelector((store) => store.userDetails);
  const [loanAmount, setLoanAmount] = useState("");
  const [loanType, setLoanType] = useState("");
  const [loanPurpose, setLoanPurpose] = useState("");
  const dispatch = useDispatch();
  const showToast = useCustomToast();

  const submitData = async () => {
    try {
      if (!loanAmount.trim() || !loanType.trim() || !loanPurpose.trim()) {
        showToast("All Fields are required", "error");
        return;
      }

      let typeOfLoan;
      if (loanType === "personal") {
        typeOfLoan = "Personal Loan";
      } else if (loanType === "business") {
        typeOfLoan = "Business Loan";
      } else {
        typeOfLoan = "Others";
      }
      const response = await createLoanApplication(
        loanAmount,
        typeOfLoan,
        loanPurpose,
        customerUUID
      );
      const applicationUUID = response[0].message.application_uuid;

      //update data into redux
      dispatch(
        setUserDetails({
          applicationUUID,
        })
      );
      showToast(response[0].data, "success");

      //going to next tab
      dispatch(setUserTimelineStep(3));

      //update status timeline
      updateStatusForTimeline(
        2,
        "loan_details",
        "loan_information",
        "completed"
      );
    } catch (error) {
      showToast(error.message, "error");
    }
  };

  return (
    <Box>
      {/* Loan Type Selection */}
      <Heading as={"h2"} fontSize={"22px"} lineHeight={"22px"}>
        Select Type of Loan
      </Heading>

      {/* Loan Amount Field */}
      <FormControl my={6} width={"50%"}>
        <FormLabel fontSize={"14px"} fontWeight={400} htmlFor="loan-amount">
          Loan Amount
        </FormLabel>
        <InputGroup>
          <InputLeftElement
            pointerEvents="none"
            display="flex"
            alignItems="center"
            mt={"6px"}
            ml={"6px"}
          >
            <LuIndianRupee className="text-orange-400 h-4 w-4" />
          </InputLeftElement>
          <Input
            name="loan-amount"
            type="text"
            value={formatNumberWithCommas(loanAmount)}
            onChange={(e) => {
              //replacing all the commas from text input
              const rawValue = e.target.value.replace(/,/g, "");
              const lastChar = rawValue[rawValue.length - 1];
              // If the last character is not a digit (0-9), do nothing
              if (!/^\d$/.test(lastChar) && rawValue) {
                showToast("Please enter valid number", "error");
                return;
              }
              setLoanAmount(rawValue);
            }}
            placeholder="Enter Amount"
            focusBorderColor="teal.500"
            pl="2.7rem"
            py={2}
            fontSize={"14px"}
            backgroundColor={"white"}
            height={"50px"}
            width={"288px"}
            borderRadius={"8px"}
            borderColor={"#E8E8E8"}
          />
        </InputGroup>
      </FormControl>

      <Box my={5} mb={5}>
        <Text fontSize={"14px"} fontWeight={400} mb={3}>
          Select Loan Type
        </Text>
        <SimpleGrid columns={2} spacing={4} columnGap={12}>
          <LoanTypeOption
            icon={FaRegUser}
            label="Personal"
            isActive={loanType === "personal"}
            onClick={() => setLoanType("personal")}
            backgroundColor={"white"}
            fontSize={"14px"}
            height={"50px"}
            width={"288px"}
            borderRadius={"8px"}
          />
          <LoanTypeOption
            icon={SlBriefcase}
            label="Business"
            isActive={loanType === "business"}
            onClick={() => setLoanType("business")}
            fontSize={"14px"}
            backgroundColor={"white"}
            height={"50px"}
            width={"288px"}
            borderRadius={"8px"}
          />
          <LoanTypeOption
            icon={FaSackDollar}
            label="Others"
            isActive={loanType === "others"}
            onClick={() => setLoanType("others")}
            fontSize={"14px"}
            backgroundColor={"white"}
            height={"50px"}
            width={"288px"}
            borderRadius={"8px"}
          />
        </SimpleGrid>
      </Box>

      {/* Purpose of Loan */}
      <Text fontSize={"14px"} fontWeight={400} my={3}>
        Purpose of Loan
      </Text>
      <Box
        width="614px"
        boxShadow="rgba(17, 17, 26, 0.1) 0px 0px 4px;"
        paddingX={"5"}
        paddingY={"10px"}
        borderRadius={"8px"}
        backgroundColor={"white"}
      >
        <RadioGroup
          name="loan-purpose"
          onChange={setLoanPurpose}
          value={loanPurpose}
          colorScheme="teal"
        >
          <Stack rowGap={"10px"}>
            {purposeOfLoan.map((value) => (
              <Radio
                key={value + "-loan-purpose"}
                textTransform={"capitalize"}
                value={value}
              >
                <Box
                  color={"#595657"}
                  ml={2}
                  fontSize={"14px"}
                  textTransform="capitalize"
                >
                  {value}
                </Box>
              </Radio>
            ))}
          </Stack>
        </RadioGroup>
      </Box>

      <Box display={"flex"} justifyContent={"flex-start"} columnGap={4} mt={5}>
        <Button
          colorScheme="blackAlpha"
          color={"blackAlpha.700"}
          backgroundColor={"white"}
          size="sm"
          variant="outline"
          width="7.5rem"
          px={8}
          onClick={() => dispatch(setUserTimelineStep(1))}
          _hover={{ backgroundColor: "#eee" }}
        >
          Back
        </Button>
        <Button
          colorScheme="teal"
          size="sm"
          variant="solid"
          px={8}
          onClick={submitData}
          width="7.5rem"
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default LoanDetails;
